import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import Culture from './Culture';
import { useCallback, useEffect, useMemo, useRef } from "react";
import Helpers from "@/Helpers";

export default function Cultures(props) {
    const { sectionData, values, setValue } = props;
    const currentSection = sectionData?.currentSection;
    const sectionLang = sectionData?.lang || "eng";
    const sectionName = currentSection?.name || "";
    const formId = currentSection?.formId || 0;
    const safeValues = useMemo(() => values || {}, [values]);
    const getCultureValue = useCallback((cultureId) => {
        const cultureKey = String(cultureId ?? "");
        const numericCultureId = Number(cultureId);
        const prefixedKey = Number.isFinite(numericCultureId)
            ? String((Number(formId || 0) + numericCultureId))
            : String(formId) + cultureKey;

        const prefixedValue = safeValues[prefixedKey];
        if (prefixedValue !== undefined && prefixedValue !== null && prefixedValue !== "") {
            return prefixedValue;
        }

        return safeValues[cultureKey];
    }, [formId, safeValues]);
    const content = useMemo(() => {
        return Helpers.getSectionContent(currentSection, sectionLang);
    }, [currentSection, sectionLang]);
    const elements = useMemo(() => Helpers.getSectionElements(content), [content]);

    const contentItems = useMemo(() => {
        return Array.isArray(elements?.items) ? elements.items : [];
    }, [elements]);

    const containerRef = useRef(null);
    const itemsToRender = contentItems;
    const instructionHtml = Helpers.getSectionHtmlValue(content, "instructions");

    const getIssueCount = useCallback((valuesToValidate) => {
        let ret = contentItems.length;
        if (!valuesToValidate)
            ret = contentItems.length; // All items need validation
        else
            ret = contentItems.length - Object.keys(valuesToValidate).length;
        return ret;
    }, [contentItems]);

    useEffect(() => {
        if (typeof sectionData?.registerSection !== "function") return;

        sectionData.registerSection( 767);
     // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [currentSection?.id]);

    useEffect(() => {
        const nextCount = Number(sectionData?.nextCount || 0);
        const issueCount = nextCount > 0 ? getIssueCount(values || {}) : 0;
        sectionData?.updateIssueCount?.(issueCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentItems.length, getIssueCount, sectionData?.nextCount, sectionData?.updateIssueCount, values]);

    const onChange = useCallback(async (childId, value) => {
        if (setValue)
            await setValue(childId, value);
        else {
            if (sectionData && sectionData.onAnswer)
                await sectionData.onAnswer(childId, value);
        }
    }, [sectionData, setValue]);

    // Add keyboard input functionality
    useEffect(() => {
        const container = containerRef.current;
        if (!container || itemsToRender.length === 0) return;

        const handleKeyDown = (e) => {
            if (sectionData?.isAdmin) {
                // Handle number keys 1-7 for rating selection
                if (e.key >= '1' && e.key <= '7') {
                    e.preventDefault();
                    const keyValue = parseInt(e.key);

                    // Find the first unfilled culture item
                    for (const culture of itemsToRender) {
                        const currentValue = getCultureValue(culture.id);

                        if (!currentValue || currentValue === 0) {
                            // Found first unfilled culture item, set its value
                            void onChange(culture.id, keyValue);
                            return;
                        }
                    }
                }
                // Handle Enter key for next page
                else if (e.key === 'Enter') {
                    e.preventDefault();

                    // Find and click the next page button
                    const nextButton = document.querySelector('.formButtons .nextSection, .formButtons .nextPage, .formButtons input[type="button"][value*="Next"]') as HTMLElement | null;
                    if (nextButton) {
                        nextButton.click();
                    } else {
                        // Fallback: look for any button with "next" in its text or value
                        const buttons = document.querySelectorAll('.formButtons button, .formButtons input[type="button"]');
                        for (const button of buttons) {
                            const typedButton = button as HTMLButtonElement | HTMLInputElement;
                            const text = (typedButton.textContent || typedButton.value || '').toLowerCase();
                            if (text.includes('next')) {
                                (typedButton as HTMLElement).click();
                                return;
                            }
                        }
                    }
                }
            }
        };

        // Use document-level listener to capture keyboard events
        const documentHandler = (e) => {
            // Check if we're on the Cultures section by checking if our container is visible
            const isVisible = container.offsetParent !== null;

            if (isVisible && currentSection?.name?.includes('Culture')) {
                handleKeyDown(e);
            }
        };

        document.addEventListener('keydown', documentHandler);

        return () => {
            document.removeEventListener('keydown', documentHandler);
        };
    }, [currentSection?.formId, currentSection?.name, getCultureValue, itemsToRender, onChange, sectionData?.isAdmin]);

    return (
        <div className={"Cultural " + sectionName + " " + sectionLang} ref={containerRef} tabIndex={0}>
            <div className="instructions">{parseSanitizedHtml(instructionHtml)}</div>
            <table className="tableData nowrap center">
                <tbody>
                    {(() => {
                        const rows = [];
                        for (let index = 0; index < itemsToRender.length; index++) {
                            const culture = itemsToRender[index];
                            rows.push(
                                <Culture
                                    key={index}
                                    culture={culture}
                                    value={getCultureValue(culture.id) || ''}
                                    showValidations={sectionData?.showValidations}
                                    setValue={onChange}
                                />
                            );
                        }
                        return rows;
                    })()}
                </tbody>
            </table>
        </div>
    );
}

